import { http } from "./http";
import type { UserDevice, DeviceApprovalStatus } from "./types";

export interface ListDevicesParams {
  status?: DeviceApprovalStatus;
  userId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ListDevicesResponse {
  data: UserDevice[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function listDeviceRequests(params?: ListDevicesParams): Promise<ListDevicesResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  if (params?.userId) query.set("userId", params.userId);
  if (params?.search) query.set("search", params.search);
  if (params?.page) query.set("page", String(params.page));
  if (params?.limit) query.set("limit", String(params.limit));

  const queryString = query.toString();
  const path = `/devices${queryString ? `?${queryString}` : ""}`;
  return http<ListDevicesResponse>(path, { method: "GET", raw: true });
}

export async function getPendingDeviceCount(): Promise<number> {
  try {
    const res = await http<{ count: number }>("/devices/pending-count", { method: "GET" });
    return res?.count || 0;
  } catch {
    return 0;
  }
}

export async function getUserDevices(userId: string): Promise<UserDevice[]> {
  return http<UserDevice[]>(`/devices/user/${userId}`, { method: "GET" });
}

export async function approveDevice(
  id: string,
  customLabel?: string
): Promise<{ device: UserDevice; autoRevokedDevice?: { id: string; label?: string | null } | null }> {
  return http<{ device: UserDevice; autoRevokedDevice?: { id: string; label?: string | null } | null }>(
    `/devices/${id}/approve`,
    {
      method: "POST",
      body: { customLabel },
    }
  );
}

export async function rejectDevice(id: string, reason?: string): Promise<UserDevice> {
  return http<UserDevice>(`/devices/${id}/reject`, {
    method: "POST",
    body: { reason },
  });
}

export async function revokeDevice(id: string, reason?: string): Promise<UserDevice> {
  return http<UserDevice>(`/devices/${id}/revoke`, {
    method: "POST",
    body: { reason },
  });
}

export async function updateDeviceLabel(id: string, label: string): Promise<UserDevice> {
  return http<UserDevice>(`/devices/${id}/label`, {
    method: "PATCH",
    body: { label },
  });
}

export async function deleteDevice(id: string): Promise<{ id: string; success: boolean }> {
  return http<{ id: string; success: boolean }>(`/devices/${id}`, {
    method: "DELETE",
  });
}

export async function checkDeviceStatus(
  email?: string,
  ipAddress?: string
): Promise<{
  ipAddress?: string | null;
  status: DeviceApprovalStatus | "UNKNOWN";
  requestedAt?: string;
  approvedAt?: string;
  rejectReason?: string;
  browser?: string;
  os?: string;
}> {
  const query = new URLSearchParams();
  if (ipAddress) query.set("ipAddress", ipAddress);
  if (email) query.set("email", email);
  return http(`/auth/device-status?${query.toString()}`, { method: "GET", withAuth: false });
}

export async function requestDeviceApproval(payload: {
  email: string;
  password: string;
  ipAddress?: string;
}): Promise<{
  success: boolean;
  ipAddress?: string;
  status: "PENDING";
  message: string;
  browser?: string;
  os?: string;
}> {
  return http(`/auth/request-device-approval`, {
    method: "POST",
    body: payload,
    withAuth: false,
  });
}

export const devicesService = {
  listDeviceRequests,
  getPendingDeviceCount,
  getUserDevices,
  approveDevice,
  rejectDevice,
  revokeDevice,
  deleteDevice,
  updateDeviceLabel,
  checkDeviceStatus,
  requestDeviceApproval,
};
